import React from 'react'
import { Check, RefreshCw, Sparkles } from 'lucide-react'
import { ApiError, apiFetch } from '../../lib/api-client'
import { useAuth } from '../../lib/auth/AuthContext'
import { Button } from '../../components/ui/Button'
import { ComplianceDisclaimer } from '../../components/ComplianceDisclaimer'
import { fmtDate, whoPaysLabel, WHO_PAYS_LABELS, WHO_PAYS_OPTIONS, type WhoPays } from './format'

/** The who-pays fields of a henvendelse, as returned by GET /api/v1/henvendelser. */
export interface WhoPaysFields {
  id: string
  who_pays: string | null
  who_pays_suggestion: string | null
  who_pays_rationale: string | null
  who_pays_suggested_at: string | null
  who_pays_confirmed_by: string | null
  who_pays_confirmed_by_name: string | null
  who_pays_confirmed_at: string | null
}

interface AssessResponse {
  id: string
  who_pays_suggestion: string
  who_pays_rationale: string
  who_pays_suggested_at: string
}

interface ConfirmResponse {
  id: string
  who_pays: string
  who_pays_suggestion: string | null
  who_pays_confirmed_by: string
  who_pays_confirmed_at: string
}

function errorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 403) return 'Din rolle har ikke adgang til at vurdere eller bekræfte, hvem der betaler.'
  if (e instanceof ApiError && e.status === 429) return 'For mange forsøg på kort tid. Vent et minut, og prøv igen.'
  return e instanceof Error && e.message && e.message !== 'Request failed' ? e.message : fallback
}

function asWhoPays(v: string | null): WhoPays | null {
  return v && (WHO_PAYS_OPTIONS as string[]).includes(v) ? (v as WhoPays) : null
}

export function WhoPaysPanel({
  row,
  onChange,
}: {
  row: WhoPaysFields
  onChange: (patch: Partial<WhoPaysFields>) => void
}) {
  const { user } = useAuth()
  const [choice, setChoice] = React.useState<WhoPays | null>(
    asWhoPays(row.who_pays) ?? asWhoPays(row.who_pays_suggestion),
  )
  const [assessing, setAssessing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const busy = assessing || saving
  const hasSuggestion = !!row.who_pays_suggestion

  async function assess() {
    setAssessing(true)
    setError(null)
    try {
      const res = await apiFetch<AssessResponse>(`/api/v1/henvendelser/${row.id}/who-pays/assess`, {
        method: 'POST',
      })
      onChange({
        who_pays_suggestion: res.who_pays_suggestion,
        who_pays_rationale: res.who_pays_rationale,
        who_pays_suggested_at: res.who_pays_suggested_at,
      })
      // Pre-select the new suggestion unless the landlord already has a confirmed choice.
      if (!row.who_pays) setChoice(asWhoPays(res.who_pays_suggestion))
    } catch (e: unknown) {
      setError(errorText(e, 'Kunne ikke lave et forslag. Prøv igen senere.'))
    } finally {
      setAssessing(false)
    }
  }

  async function confirm() {
    if (!choice) return
    setSaving(true)
    setError(null)
    try {
      const res = await apiFetch<ConfirmResponse>(`/api/v1/henvendelser/${row.id}/who-pays/confirm`, {
        method: 'POST',
        body: JSON.stringify({ who_pays: choice }),
      })
      onChange({
        who_pays: res.who_pays,
        who_pays_confirmed_by: res.who_pays_confirmed_by,
        // Backend shows COALESCE(name, email) for the confirmer; mirror that locally.
        who_pays_confirmed_by_name: user?.name || user?.email || null,
        who_pays_confirmed_at: res.who_pays_confirmed_at,
      })
    } catch (e: unknown) {
      setError(errorText(e, 'Kunne ikke gemme valget. Prøv igen.'))
    } finally {
      setSaving(false)
    }
  }

  const unchanged = !!row.who_pays && choice === row.who_pays
  const deviates = hasSuggestion && choice !== null && choice !== row.who_pays_suggestion
  const confirmLabel = unchanged ? 'Bekræftet' : deviates ? 'Gem ændring' : 'Bekræft'

  return (
    <section aria-label="Hvem betaler?" style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Hvem betaler?</div>
        <Button
          variant="secondary"
          size="sm"
          isLoading={assessing}
          disabled={busy}
          onClick={() => void assess()}
        >
          {!assessing && (hasSuggestion ? <RefreshCw size={13} /> : <Sparkles size={13} />)}
          {assessing ? 'Laver forslag…' : hasSuggestion ? 'Lav nyt forslag' : 'Lav AI-forslag'}
        </Button>
      </div>

      {hasSuggestion ? (
        <div style={suggestionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={badgeStyle}>
              <Sparkles size={12} aria-hidden />
              AI-forslag, ikke juridisk rådgivning
            </span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtDate(row.who_pays_suggested_at)}</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 14, color: '#111827' }}>
            Forslag: <strong>{whoPaysLabel(row.who_pays_suggestion)}</strong>
          </div>
          {row.who_pays_rationale && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#374151', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {row.who_pays_rationale}
            </p>
          )}
        </div>
      ) : (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: '#6b7280' }}>
          Der er ikke lavet et forslag endnu.
        </p>
      )}

      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div role="radiogroup" aria-label="Hvem betaler" style={segmentGroupStyle}>
          {WHO_PAYS_OPTIONS.map((opt) => {
            const selected = choice === opt
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={busy}
                onClick={() => setChoice(opt)}
                style={{
                  ...segmentStyle,
                  background: selected ? '#ffffff' : 'transparent',
                  color: selected ? '#111827' : '#6b7280',
                  fontWeight: selected ? 600 : 500,
                  boxShadow: selected ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {WHO_PAYS_LABELS[opt]}
              </button>
            )
          })}
        </div>
        <Button
          size="sm"
          isLoading={saving}
          disabled={busy || choice === null || unchanged}
          onClick={() => void confirm()}
        >
          {unchanged && <Check size={13} />}
          {confirmLabel}
        </Button>
      </div>

      {row.who_pays && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={14} aria-hidden />
          <span>
            Bekræftet: <strong>{whoPaysLabel(row.who_pays)}</strong>
            {row.who_pays_confirmed_by_name ? ` af ${row.who_pays_confirmed_by_name}` : ''}
            {row.who_pays_confirmed_at ? `, ${fmtDate(row.who_pays_confirmed_at)}` : ''}
          </span>
        </div>
      )}

      {error && (
        <div role="alert" style={{ marginTop: 10, fontSize: 13, color: '#dc2626' }}>
          {error}
        </div>
      )}

      <p style={{ margin: '12px 0 0', fontSize: 12, color: '#6b7280' }}>
        Forslaget vises kun for dig og sendes ikke til lejeren.
      </p>
      <ComplianceDisclaimer style={{ marginTop: 6 }} />
    </section>
  )
}

const panelStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #eef0f4',
  borderRadius: 10,
  padding: 16,
  maxWidth: 720,
}

const suggestionStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 12,
  borderRadius: 8,
  background: '#f8fafc',
  border: '1px solid #eef0f4',
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
  color: '#4338ca',
  background: '#eef2ff',
  padding: '2px 8px',
  borderRadius: 20,
  whiteSpace: 'nowrap',
}

const segmentGroupStyle: React.CSSProperties = {
  display: 'inline-flex',
  padding: 3,
  gap: 2,
  borderRadius: 8,
  background: '#f1f5f9',
}

const segmentStyle: React.CSSProperties = {
  height: 26,
  padding: '0 12px',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
}
